<app-app-layout>
  <div class="billing-container">
    <!-- Progress Bar -->
    <app-simple-stepper [currentStep]="2" [totalSteps]="3"></app-simple-stepper>

    <!-- Loading Indicator -->
    <div *ngIf="isLoading" class="loading-container">
      <mat-spinner diameter="50"></mat-spinner>
      <p>Loading billing information...</p>
    </div>

    <!-- Billing Form -->
    <div class="billing-form-card" *ngIf="!isLoading">
      <div class="form-header">
        <h2>Billing Information</h2>
        <p>Please provide your billing details for invoice generation</p>

        <!-- Validation Error Message -->
        <div *ngIf="showValidationError" class="validation-error-message">
          <mat-icon>error</mat-icon>
          <div class="error-content">
            <span class="error-title">Please complete all required fields</span>
            <span class="error-subtitle"
              >Check the highlighted fields below and fill in the missing
              information</span
            >
          </div>
        </div>
      </div>

      <!-- Indian Form -->
      <form
        *ngIf="!isInternationalPayment"
        [formGroup]="indianForm"
        (ngSubmit)="continueToReview()"
      >
        <!-- Country Selection -->
        <div class="form-section">
          <h3>Location</h3>

          <div class="form-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Country</mat-label>
              <input
                matInput
                formControlName="country"
                [matAutocomplete]="countryAuto"
                placeholder="Search and select country"
                required
                (input)="filterCountries($event.target.value)"
              />
              <mat-autocomplete
                #countryAuto="matAutocomplete"
                [displayWith]="displayCountry"
              >
                <mat-option
                  *ngFor="let country of filteredCountries"
                  [value]="country"
                >
                  {{ country.label }}
                </mat-option>
              </mat-autocomplete>
              <mat-error>Country is required</mat-error>
            </mat-form-field>
          </div>

          <!-- Domestic Address Fields -->
          <div class="domestic-fields">
            <div class="form-row">
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>State</mat-label>
                <input
                  matInput
                  formControlName="state"
                  [matAutocomplete]="stateAuto"
                  placeholder="Search and select state"
                  required
                  (input)="filterStates($event.target.value)"
                  (selectionChange)="loadCities()"
                />
                <mat-autocomplete
                  #stateAuto="matAutocomplete"
                  [displayWith]="displayState"
                >
                  <mat-option
                    *ngFor="let state of filteredStates"
                    [value]="state"
                  >
                    {{ state.state }}
                  </mat-option>
                </mat-autocomplete>
                <mat-error>State is required</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="half-width">
                <mat-label>District</mat-label>
                <input
                  matInput
                  formControlName="district"
                  [matAutocomplete]="districtAuto"
                  placeholder="Search and select district"
                  required
                  [disabled]="!indianForm.get('state')?.value"
                  (input)="filterDistricts($event.target.value)"
                />
                <mat-autocomplete
                  #districtAuto="matAutocomplete"
                  [displayWith]="displayDistrict"
                >
                  <mat-option
                    *ngFor="let dist of filteredDistricts"
                    [value]="dist"
                  >
                    {{ dist }}
                  </mat-option>
                </mat-autocomplete>
                <mat-error>District is required</mat-error>
              </mat-form-field>
            </div>

            <div class="form-row">
              <mat-form-field appearance="outline" class="half-width">
                <mat-label>Pincode</mat-label>
                <input
                  matInput
                  formControlName="pinCode"
                  required
                  type="number"
                  maxlength="6"
                  minlength="6"
                />
                <mat-error>Valid 6-digit pincode required</mat-error>
              </mat-form-field>
            </div>
          </div>
        </div>

        <!-- Address -->
        <div class="form-section">
          <h3>Address</h3>

          <div class="form-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Address Line</mat-label>
              <textarea
                matInput
                formControlName="addressLine"
                rows="3"
                required
              ></textarea>
              <mat-error>Address is required</mat-error>
            </mat-form-field>
          </div>

          <div class="form-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Gym Name (Optional)</mat-label>
              <input matInput formControlName="gymName" />
            </mat-form-field>
          </div>
        </div>

        <!-- GST Information -->
        <div class="form-section gst-section">
          <h3>GST Information</h3>

          <div class="gst-note">
            <strong>Note:</strong> All domestic payments are subject to 18% GST,
            even if you don't have a GST number.
          </div>

          <div class="form-row">
            <label class="gst-toggle-label">Are you registered for GST?</label>
            <mat-radio-group formControlName="hasGST" class="gst-radio-group">
              <mat-radio-button [value]="true">Yes</mat-radio-button>
              <mat-radio-button [value]="false">No</mat-radio-button>
            </mat-radio-group>
          </div>

          <div *ngIf="indianForm.get('hasGST')?.value" class="form-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>GST Number</mat-label>
              <input
                matInput
                formControlName="gstNumber"
                placeholder="22AAAAA0000A1Z5"
              />
              <mat-hint>Format: 22AAAAA0000A1Z5</mat-hint>
              <mat-error
                *ngIf="indianForm.get('gstNumber')?.hasError('pattern')"
              >
                Invalid GST format
              </mat-error>
            </mat-form-field>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="form-actions">
          <button
            type="button"
            mat-button
            class="back-button"
            (click)="goBack()"
          >
            <mat-icon>arrow_back</mat-icon>
            Back to Plans
          </button>

          <button
            type="submit"
            mat-raised-button
            color="primary"
            class="continue-button"
            [disabled]="isSaving"
          >
            <mat-spinner
              *ngIf="isSaving"
              diameter="20"
              class="button-spinner"
            ></mat-spinner>
            <span *ngIf="!isSaving">Continue to Review</span>
            <span *ngIf="isSaving">Saving...</span>
            <mat-icon *ngIf="!isSaving">arrow_forward</mat-icon>
          </button>
        </div>
      </form>

      <!-- International Form -->
      <form
        *ngIf="isInternationalPayment"
        [formGroup]="internationalForm"
        (ngSubmit)="continueToReview()"
      >
        <!-- Country Selection -->
        <div class="form-section">
          <h3>Location</h3>

          <div class="form-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Country</mat-label>
              <input
                matInput
                formControlName="country"
                [matAutocomplete]="countryAutoInt"
                placeholder="Search and select country"
                required
                (input)="filterCountries($event.target.value)"
              />
              <mat-autocomplete
                #countryAutoInt="matAutocomplete"
                [displayWith]="displayCountry"
              >
                <mat-option
                  *ngFor="let country of filteredCountries"
                  [value]="country"
                >
                  {{ country.label }}
                </mat-option>
              </mat-autocomplete>
              <mat-error>Country is required</mat-error>
            </mat-form-field>
          </div>
        </div>

        <!-- Address -->
        <div class="form-section">
          <h3>Address</h3>

          <div class="form-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Address Line</mat-label>
              <textarea
                matInput
                formControlName="addressLine"
                rows="3"
                required
              ></textarea>
              <mat-error>Address is required</mat-error>
            </mat-form-field>
          </div>

          <div class="form-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Gym Name (Optional)</mat-label>
              <input matInput formControlName="gymName" />
            </mat-form-field>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="form-actions">
          <button
            type="button"
            mat-button
            class="back-button"
            (click)="goBack()"
          >
            <mat-icon>arrow_back</mat-icon>
            Back to Plans
          </button>

          <button
            type="submit"
            mat-raised-button
            color="primary"
            class="continue-button"
            [disabled]="isSaving"
          >
            <mat-spinner
              *ngIf="isSaving"
              diameter="20"
              class="button-spinner"
            ></mat-spinner>
            <span *ngIf="!isSaving">Continue to Review</span>
            <span *ngIf="isSaving">Saving...</span>
            <mat-icon *ngIf="!isSaving">arrow_forward</mat-icon>
          </button>
        </div>
      </form>
    </div>
  </div>
</app-app-layout>
